/**
 * CapturedItemsPanel (ported from Noteble — ADR-022).
 *
 * Displays all captured items (live recording, voice transcripts, photos,
 * notes) before submission. Two variants:
 * - "full" (default): detailed list with view/discard actions (capture step)
 * - "summary": compact read-only bar with item counts (template step)
 */

import React, { useRef, useEffect, useState } from 'react';
import {
  Mic, Image, StickyNote, FileText, Clock, Trash2, Languages,
  Check, AlertCircle, Sparkles,
} from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { ListItemCard } from './ListItemCard';
import { SavedItemTitle } from './SavedItemTitle';
import type { CapturedPhoto, RecordingNote, UploadStatusEntry } from './AddContext';
import type { CapturedStream } from './workflow';

export const AI_DRAFT_TITLE = 'AI Document Draft';

interface CapturedItemsPanelProps {
  variant?: 'full' | 'summary';
  streamingTranscripts: CapturedStream[];
  photos: CapturedPhoto[];
  notes: RecordingNote[];
  uploadStatuses?: Map<string, UploadStatusEntry>;
  onDiscardStreamingTranscript?: (id: string) => void;
  onViewStreamingTranscript?: (transcript: CapturedStream) => void;
  onRetryUpload?: (id: string) => void;
  onViewNote?: (note: RecordingNote) => void;
  onRemoveNote?: (noteId: string) => void;
  onRemovePhoto?: (photoId: string) => void;
  onViewPhoto?: (photo: CapturedPhoto) => void;
  onPreviewPhoto?: (photoId: string) => void;
  /** Live voice transcript — shown as a live entry at the top of the list. */
  liveStreamingTranscript?: {
    finalizedText: string;
    wordCount: number;
    isTruncated: boolean;
    isActive: boolean;
  };
  onStopStreaming?: () => void;
  onDiscardLiveStreaming?: () => void;
  streamingDuration?: number;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/** Plain-text preview of a markdown note (headings/emphasis/links stripped). */
export function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[*_~>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const UploadIndicator: React.FC<{ status?: UploadStatusEntry; onRetry?: () => void }> = ({ status, onRetry }) => {
  if (!status) return null;

  if (status.status === 'uploading') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
        <Spinner size="sm" />
        {status.progress}%
      </span>
    );
  }

  if (status.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1">
        <AlertCircle className="w-3.5 h-3.5 text-red-500" />
        {onRetry && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRetry(); }}
            className="text-xs text-red-600 hover:text-red-800 font-medium underline"
          >
            Retry
          </button>
        )}
      </span>
    );
  }

  return null;
};

const PhotoThumbnail: React.FC<{ blob: Blob; onClick?: () => void }> = ({ blob, onClick }) => {
  const [url, setUrl] = useState<string>('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onClick?.(); } }}
      className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-emerald-100 dark:bg-emerald-900/40 hover:ring-2 hover:ring-emerald-500/50 transition-all cursor-pointer"
      aria-label="Preview photo"
    >
      {url ? (
        <img src={url} alt="Photo thumbnail" className="w-full h-full object-cover" />
      ) : (
        <Image className="w-5 h-5 text-emerald-600 m-auto mt-2.5" />
      )}
    </div>
  );
};

const DiscardButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onClick(); } }}
    className="flex flex-col items-center gap-0.5 w-12 py-0.5 rounded-2xl transition-all duration-200 active:scale-95 text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 cursor-pointer"
    aria-label="Discard"
  >
    <div className="relative flex items-center justify-center w-10 h-6 rounded-full"><Trash2 className="w-4 h-4" strokeWidth={1.6} /></div>
  </div>
);

export const CapturedItemsPanel: React.FC<CapturedItemsPanelProps> = ({
  variant = 'full',
  streamingTranscripts,
  photos,
  notes,
  uploadStatuses,
  onDiscardStreamingTranscript,
  onViewStreamingTranscript,
  onRetryUpload,
  onViewNote,
  onRemoveNote,
  onRemovePhoto,
  onViewPhoto,
  onPreviewPhoto,
  liveStreamingTranscript,
  onStopStreaming,
  onDiscardLiveStreaming,
  streamingDuration,
}) => {
  const liveTranscriptRef = useRef<HTMLDivElement>(null);

  const hasStreamingTranscripts = streamingTranscripts.length > 0;
  const hasPhotos = photos.length > 0;
  const hasNotes = notes.length > 0;
  const hasLiveTranscript = liveStreamingTranscript?.isActive ?? false;
  const hasAnyContent = hasStreamingTranscripts || hasPhotos || hasNotes || hasLiveTranscript;

  // Auto-scroll live transcript entry
  useEffect(() => {
    if (liveTranscriptRef.current) {
      liveTranscriptRef.current.scrollTop = liveTranscriptRef.current.scrollHeight;
    }
  }, [liveStreamingTranscript?.finalizedText]);

  // ──────────────────────────────────────────
  // Summary variant — compact bar for the template step
  // ──────────────────────────────────────────
  if (variant === 'summary') {
    if (!hasAnyContent) {
      return (
        <div className="mb-6 border border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-4">
          <span className="text-base text-slate-400 dark:text-slate-500 select-none">
            No items captured
          </span>
        </div>
      );
    }

    const photoUploadedCount = photos.filter((p) => uploadStatuses?.get(p.id)?.status === 'uploaded' || p.mediaId).length;
    const photoUploadingCount = photos.filter((p) => uploadStatuses?.get(p.id)?.status === 'uploading').length;
    const photoFailedCount = photos.filter((p) => uploadStatuses?.get(p.id)?.status === 'failed').length;
    const docNotes = notes.filter((n) => n.sourceType === 'document');
    const regularNotes = notes.filter((n) => n.sourceType !== 'document');

    return (
      <div className="mb-6 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Captured items:
            </span>
            {hasStreamingTranscripts && (
              <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2.5 py-1 rounded-xl">
                <Languages className="w-3.5 h-3.5" />
                {streamingTranscripts.length}
              </span>
            )}
            {hasPhotos && (
              <span className="inline-flex items-center gap-1.5 text-sm text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-xl">
                <Image className="w-3.5 h-3.5" />
                {photos.length}
                {photoUploadingCount > 0 && (
                  <Spinner size="sm" tone="current" />
                )}
                {photoUploadedCount === photos.length && photos.length > 0 && photoUploadingCount === 0 && photoFailedCount === 0 && (
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                )}
                {photoFailedCount > 0 && (
                  <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                )}
              </span>
            )}
            {regularNotes.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2.5 py-1 rounded-xl">
                <StickyNote className="w-3.5 h-3.5" />
                {regularNotes.length}
              </span>
            )}
            {docNotes.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-sm text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-xl">
                <FileText className="w-3.5 h-3.5" />
                {docNotes.length}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────
  // Full variant — detailed list with actions
  // ──────────────────────────────────────────
  if (!hasAnyContent) return null;

  return (
    <div className="space-y-3">
      {/* Live voice recording — transcript grows in place until Capture */}
      {hasLiveTranscript && liveStreamingTranscript && (
        <ListItemCard
          icon={
            <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-xl relative">
              <Mic className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            </div>
          }
          onSend={onStopStreaming}
          sendLabel="Capture"
          onDiscard={onDiscardLiveStreaming}
          discardLabel="Discard"
        >
          <p className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            Recording
          </p>
          <div className="flex items-center gap-2">
            {typeof streamingDuration === 'number' && (
              <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDuration(streamingDuration)}
              </p>
            )}
            {liveStreamingTranscript.wordCount > 0 && (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {liveStreamingTranscript.wordCount} words
              </span>
            )}
          </div>
          {/* Live transcript preview */}
          <div
            ref={liveTranscriptRef}
            className="max-h-20 overflow-y-auto text-xs text-slate-600 dark:text-slate-400 leading-relaxed mt-1"
          >
            {liveStreamingTranscript.isTruncated && <span className="text-slate-400">&hellip; </span>}
            {liveStreamingTranscript.finalizedText
              ? <span>{liveStreamingTranscript.finalizedText}</span>
              : <span className="italic">Listening for speech…</span>}
          </div>
        </ListItemCard>
      )}

      {/* Voice transcripts (already transcribed) */}
      {streamingTranscripts.map((transcript, index) => {
        const isClickable = !!(onViewStreamingTranscript && transcript.transcript);
        return (
          <ListItemCard
            key={transcript.id}
            onClick={isClickable ? () => onViewStreamingTranscript(transcript) : undefined}
            icon={
              <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-xl">
                <Mic className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
            }
            right={onDiscardStreamingTranscript ? (
              <DiscardButton onClick={() => onDiscardStreamingTranscript(transcript.id)} />
            ) : undefined}
          >
            <SavedItemTitle label="Voice transcript" index={index} duration={transcript.duration} />
            {!transcript.transcript && (
              <span className="text-xs text-slate-400">No transcription</span>
            )}
            {transcript.transcript && (
              <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-1">
                {transcript.transcript.slice(0, 120)}{transcript.transcript.length > 120 ? '…' : ''}
              </p>
            )}
          </ListItemCard>
        );
      })}

      {/* Photos (per-photo upload status, retry, description, delete) */}
      {photos.map((photo, index) => {
        const uploadStatus = uploadStatuses?.get(photo.id);
        return (
          <ListItemCard
            key={photo.id}
            onClick={onViewPhoto ? () => onViewPhoto(photo) : undefined}
            icon={<PhotoThumbnail blob={photo.blob} onClick={onPreviewPhoto ? () => onPreviewPhoto(photo.id) : undefined} />}
            right={onRemovePhoto ? <DiscardButton onClick={() => onRemovePhoto(photo.id)} /> : undefined}
          >
            <SavedItemTitle label="Photo" index={index} saved={uploadStatus?.status === 'uploaded' || !!photo.mediaId} />
            <div className="flex items-center gap-2">
              {photo.description && (
                <p className="text-sm text-slate-500 dark:text-slate-400 truncate flex-1">
                  {stripMarkdown(photo.description)}
                </p>
              )}
              <UploadIndicator
                status={uploadStatus}
                onRetry={onRetryUpload ? () => onRetryUpload(photo.id) : undefined}
              />
            </div>
          </ListItemCard>
        );
      })}

      {/* Notes */}
      {notes.map((note, index) => {
        const isDocument = note.sourceType === 'document';
        const isAiDraft = note.sourceTitle === AI_DRAFT_TITLE;
        return (
          <ListItemCard
            key={note.id}
            onClick={onViewNote ? () => onViewNote(note) : undefined}
            icon={
              isAiDraft ? (
                <div className="p-2 bg-violet-100 dark:bg-violet-900/50 rounded-xl">
                  <Sparkles className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
              ) : isDocument ? (
                <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-xl">
                  <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
              ) : (
                <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-xl">
                  <StickyNote className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
              )
            }
            right={onRemoveNote ? <DiscardButton onClick={() => onRemoveNote(note.id)} /> : undefined}
          >
            <SavedItemTitle label={isDocument ? (note.sourceTitle || 'Document') : 'Note'} index={isDocument ? undefined : index} />
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{stripMarkdown(note.text)}</p>
          </ListItemCard>
        );
      })}
    </div>
  );
};
